import { ApolloLink } from '@apollo/client';
import { concat, Observable } from 'rxjs';
import { debounceTime, first, skip } from 'rxjs/operators';
import type { HttpLink } from 'apollo-angular/http';
import type { ExecutionResult, GraphQLError } from 'graphql';

const apolloProperties = ['__typename', 'Symbol(id)'];

export function apolloClean(data, removeApolloProperties = true) {
  if (Array.isArray(data)) {
    return data.map(value => apolloClean(value, removeApolloProperties));
  } else if (data !== null && typeof data === 'object') {
    return Object.keys(data).reduce((dataCopy, key) => {
      if (removeApolloProperties === false || !apolloProperties.includes(key)) {
        dataCopy[key] = apolloClean(data[key], removeApolloProperties);
      }
      return dataCopy;
    }, {});
  }

  return data;
}

export function debounceAfterFirst(observable: Observable<any>, debounce: number) {
  return concat(observable.pipe(first()), observable.pipe(skip(1), debounceTime(debounce)));
}

export interface GraphQLInput<T> {
  input: T;
}


/**
 * Following part is fork of apollo-link-error repository as there were some customizations needed
 * TODO: check later if official repo is working as expected
 */
export interface ErrorResponse {
  graphQLErrors?: GraphQLError[];
  networkError?: Error;
  response?: ExecutionResult;
  operation: ApolloLink.Operation;
}

export namespace ErrorLink {
  /**
   * Callback to be triggered when an error occurs within the link stack.
   */
  export interface ErrorHandler {
    (error: ErrorResponse): void;
  }
}

// For backwards compatibility.
export import ErrorHandler = ErrorLink.ErrorHandler;

export const onError = (errorHandler: ErrorHandler): ApolloLink => {
  return new ApolloLink((operation, forward) => {
    return new Observable(observer => {
      let sub;
      try {
        sub = forward(operation).subscribe({
          next: result => {
            if (result.errors) {
              errorHandler({
                graphQLErrors: result.errors as any, // TODO: Fix typing
                response: result as any, // TODO: Fix typing
                operation,
              });
            }
            observer.next(result);
          },
          error: networkError => {
            errorHandler({
              operation,
              networkError,
              // Network errors can return GraphQL errors on for example a 403
              graphQLErrors: networkError.error && networkError.error.errors
            });
            observer.error(new ApolloError({
              graphQLErrors: networkError.error && networkError.error.errors,
              networkError
            }));
          },
          complete: observer.complete.bind(observer),
        });
      } catch (e) {
        errorHandler({ networkError: e, operation });
        observer.error(e);
      }

      return () => {
        if (sub) {
          sub.unsubscribe();
        }
      };
    });
  });
};

export class ApolloError extends Error {
  name: string;
  message: string;
  graphQLErrors: GraphQLError[];
  networkError: Error;

  constructor(
    data: {
      graphQLErrors: GraphQLError[];
      networkError: Error;
      errorMessage?: string;
    }
  ) {
    super(data.errorMessage);
    this.graphQLErrors = data.graphQLErrors;
    this.networkError = data.networkError;
    this.message = data.errorMessage || '';
  }
}

export interface APIErrorContext {
  networkError: Error;
  graphQLErrors: any[];
  response: any;
  operation: ApolloLink.Operation;
  errorWhiteList: number[];
}

export function createDefaultAPIErrorLink(options: {
  errorHandlerService?: { handleApiError(context: APIErrorContext): void };
} = {}) {
  return onError(({ networkError, graphQLErrors, response, operation }) => {
    const context = operation.getContext();

    if (graphQLErrors) {
      graphQLErrors.forEach((graphqlError: (GraphQLError & { api_error: any })) => console.error(getFormattedGraphqlError(graphqlError)));
      console.log('[GraphQL Query]', operation.query.loc.source.body);
      console.log('[GraphQL Variables]', '\n' + JSON.stringify(operation.variables));
    }

    if (networkError) {
      console.log(`[Network error]: ${JSON.stringify(networkError)}`);
    }

    const error = context.error;
    const errorWhiteList = context && context.errorWhiteList;
    if (options.errorHandlerService && error !== false) {
      options.errorHandlerService.handleApiError({
        networkError,
        graphQLErrors,
        response,
        operation,
        errorWhiteList
      });
    }
  });
}

export class ErrorLink extends ApolloLink {
  private link: ApolloLink;

  constructor(errorHandler: ErrorLink.ErrorHandler) {
    super();
    this.link = onError(errorHandler);
  }

  public request(operation, forward): Observable<ApolloLink.Result> | null {
    return this.link.request(operation, forward);
  }
}
export function getFormattedGraphqlError(error: (GraphQLError & { api_error: any }), pretty = false) {
  // eslint:disable-next-line: max-line-length
  return `[Pipeliner GraphQL error]: Message: ${error.message}, Location: ${JSON.stringify(error.locations)}, Path: ${JSON.stringify(error.path)}${error.api_error ? `, ApiError: ${JSON.stringify(error.api_error, null, pretty ? 2 : null)}` : ''}`;
}

export interface LinkInfo {
  link: ApolloLink;
  linkOptions: HttpLink.Options;
}
